// Mock implementation of:
//   GET /api/secure/me/clinic-queue/{eventId}
//
// Wire shape mirrors clinicQueueService.js JSDoc — keep them aligned so
// backend cutover is a flag flip per resource.
//
// The mock is deterministic per (profileId, eventId): a salted hash picks the
// patient's slot, total queue size, and per-slot durations. The "currently
// being seen" pointer is computed from real wall-clock time so the UI feels
// alive every 30s without any backend infrastructure.
//
// IMPORTANT: this mock does NOT know which sessions are actually OFFLINE —
// callers (the ClinicQueueCard widget) decide which event ids to query, and
// the mock simply produces a plausible queue for whatever id it receives.

import { delay } from './index'

const CLINIC_OPEN_HOUR = 9

function hashString(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

function buildQueueShape(profileId, eventId) {
  const seed = hashString(`${profileId || 'anon'}::${eventId}`)

  // Plausible variability — total slots between 6–10, durations 20–35 min.
  const totalAppointmentsToday = 6 + (seed % 5) // 6..10
  const averageDurationMinutes = 20 + ((seed >> 3) % 16) // 20..35

  // Patient's slot is somewhere in the second half so the queue card always
  // has something interesting to show (people ahead, wait time > 0).
  const yourAppointmentNumber =
    Math.max(2, Math.min(totalAppointmentsToday, ((seed >> 7) % totalAppointmentsToday) + 2))

  return {
    totalAppointmentsToday,
    averageDurationMinutes,
    yourAppointmentNumber,
    yourDurationMinutes: averageDurationMinutes,
  }
}

function computeCurrentlySeeing(shape, now) {
  // Clinic opens at CLINIC_OPEN_HOUR local time. Each slot uses
  // averageDurationMinutes. currentAppointmentNumber is the slot whose window
  // contains "now" — null if before clinic open, totalAppointmentsToday + 1
  // (capped) if past close.
  const open = new Date(now)
  open.setHours(CLINIC_OPEN_HOUR, 0, 0, 0)
  const elapsedMin = (now.getTime() - open.getTime()) / 60000
  if (elapsedMin < 0) {
    return { currentAppointmentNumber: null, currentSessionStartedAt: null }
  }
  const slotIndex = Math.floor(elapsedMin / shape.averageDurationMinutes)
  if (slotIndex >= shape.totalAppointmentsToday) {
    return {
      currentAppointmentNumber: shape.totalAppointmentsToday,
      currentSessionStartedAt: new Date(
        open.getTime() + (shape.totalAppointmentsToday - 1) * shape.averageDurationMinutes * 60000,
      ).toISOString(),
    }
  }
  return {
    currentAppointmentNumber: slotIndex + 1, // 1-based
    currentSessionStartedAt: new Date(
      open.getTime() + slotIndex * shape.averageDurationMinutes * 60000,
    ).toISOString(),
  }
}

function nowPlusMin(min) {
  return new Date(Date.now() + min * 60 * 1000).toISOString()
}

export async function getQueueStatus(profile, eventId) {
  await delay(120)
  const profileId = profile?.id || profile?.profileId || 'anon'
  const shape = buildQueueShape(profileId, eventId)
  const now = new Date()
  const { currentAppointmentNumber, currentSessionStartedAt } = computeCurrentlySeeing(shape, now)

  // Wait estimate: number of slots between current and yours, multiplied by
  // averageDurationMinutes. If clinic hasn't opened, fall back to the gap
  // between now and your nominal slot start.
  let estimatedWaitMinutes = 0
  if (currentAppointmentNumber == null) {
    const open = new Date(now)
    open.setHours(CLINIC_OPEN_HOUR, 0, 0, 0)
    estimatedWaitMinutes = Math.max(
      0,
      Math.round(
        (open.getTime() - now.getTime()) / 60000 +
          (shape.yourAppointmentNumber - 1) * shape.averageDurationMinutes,
      ),
    )
  } else if (currentAppointmentNumber >= shape.yourAppointmentNumber) {
    estimatedWaitMinutes = 0
  } else {
    estimatedWaitMinutes =
      (shape.yourAppointmentNumber - currentAppointmentNumber) * shape.averageDurationMinutes
  }

  // Mock chamber name varies a tiny bit by seed so different events look
  // distinct in a multi-event demo.
  const chamberRoomNames = ['Chamber A', 'Chamber B', 'Chamber C', 'Consult Room 1', 'Consult Room 2']
  const chamberRoomName = chamberRoomNames[hashString(eventId) % chamberRoomNames.length]

  return {
    eventId,
    sessionMode: 'OFFLINE',
    chamberRoomId: `room_${hashString(eventId) % 99}`,
    chamberRoomName,
    practitionerId: null,
    practitionerName: null,
    yourAppointmentNumber: shape.yourAppointmentNumber,
    yourScheduledAt: nowPlusMin(
      Math.max(0, (shape.yourAppointmentNumber - (currentAppointmentNumber || 1)) * shape.averageDurationMinutes),
    ),
    yourDurationMinutes: shape.yourDurationMinutes,
    currentAppointmentNumber,
    currentSessionStartedAt,
    totalAppointmentsToday: shape.totalAppointmentsToday,
    estimatedWaitMinutes,
    averageDurationMinutes: shape.averageDurationMinutes,
    serverTime: now.toISOString(),
  }
}
